<template>
  <b-tabs pills content-class="mt-2 mb-4" v-if="value" v-model="tabIndex">
    <loading-modal ref="loadingModal" />

    <!--
    ============================================================================
    TAB Generale
    ============================================================================
    -->
    <b-tab>
      <template slot="title">
        Generale
        <b-badge variant="danger"
          v-if="countErrors(['title', 'description'])">
          {{ countErrors(['title', 'description']) }}
        </b-badge>
      </template>
      <form>
        <user-form-field
          label="Proprietario"
          required
          :value="value.owner"
          disabled
          :error="findError('owner')"
        />

        <workgroup-form-field
          label="Gruppo di lavoro"
          required
          :value="value.analysis.workgroup"
          disabled
        />

        <string-form-field
          label="Titolo"
          required
          :value="value.title"
          :disabled="disabled"
          :error="findError('title')"
          @input="propertyChanged('title', $event)"
        />

        <string-form-field
          label="Descrizione"
          :value="value.description"
          :disabled="disabled"
          :error="findError('description')"
          multiline
          @input="propertyChanged('description', $event)"
        />
      </form>
    </b-tab>
    <!--
    ============================================================================
    TAB Centrale rischi
    ============================================================================
    -->
    <b-tab>
      <template slot="title">
        Centrale rischi
        <b-badge variant="danger"
          v-if="countErrors('uploadedFiles')">
          {{ countErrors('uploadedFiles') }}
        </b-badge>
      </template>
      <template v-if="!disabled">
        <file-table
          class="mb-4"
          ref="fileTable"
          restrict="*.pdf"
          label="Caricare qui i file pdf della centrale rischi, trascinando i file nel campo sottostante o utilizzando l'apposito pulsante."
          :value="pendingFiles"
          @input="pendingFiles = $event"
          @upload="upload($event)">
          <b-alert slot="advice" show variant="info">
            <strong>N.B.</strong> Il nome di ciascun file deve terminare con
            <strong>.pdf</strong>, altrimenti il file verrà scartato.
          </b-alert>
        </file-table>
        <div v-if="value.uploadedFiles.length" class="content-separator"></div>
      </template>

      <div v-if="value.uploadedFiles.length">
        <data-table
          :columns="uploadedFilesColumns"
          :value="value.uploadedFiles"
        />

        <b-alert show variant="info">
          <strong>N.B.</strong> Cliccando sul pulsante <strong>Estrai</strong>,
          il software estrarrà i dati dai file caricati. Si consiglia di
          controllare i dati estratti per verificarne la correttezza/completezza
          prima di selezionare il tab <strong>Analisi</strong> e di procedere
          oltre.
        </b-alert>

        <div class="text-right">
          <b-button
            v-if="!disabled"
            variant="success"
            @click.stop.prevent="extractData()">
            <i class="fa fa-fw fa-area-chart"></i>
            Estrai
          </b-button>
        </div>
      </div>

      <b-modal
        ref="extractionAdvice"
        size="lg"
        scrollable
        ok-only
        title="Estrazione dati completata con successo.">
        <p>
          Si consiglia di controllare i dati estratti per verificarne
          la correttezza/completezza.
        </p>

        <p>
          Una volta controllati/completati i dati in questo tab, si può
          selezionare il tab <strong>Analisi</strong> e procedere
          con l'elaborazione dei dati.
        </p>
        <p>
          <strong>N.B.: I dati modificati manualmente NON vengono salvati
          cliccando sul pulsante Salva a meno che non sia stata eseguita
          un'analisi con i dati modificati.</strong> Questo per evitare che
          chi apre l'analisi in futuro possa essere tratto in inganno da dati
          di input diversi da quelli realmente utilizzati.
        </p>
      </b-modal>

      <template v-if="input && input.tables && input.tables.length">
        <div class="content-separator my-4"></div>
        <div class="text-right">
          <b-dropdown variant="primary">
            <template slot="button-content">
              <i class="fa fa-fw fa-sign-out"></i>
              Esporta
            </template>
            <b-dropdown-item-button @click.stop.prevent="exportData('xlsx')">
              Excel (xlsx)
            </b-dropdown-item-button>
            <b-dropdown-item-button @click.stop.prevent="exportData('csv')">
              Testo (csv)
            </b-dropdown-item-button>
          </b-dropdown>
        </div>

        <b-tabs pills content-class="mt-4 mb-4 mx-0 px-0 border-0">
          <template v-for="t in input.tables">
            <b-tab :title="t.title" class="py-0 px-0 mx-0">
              <cr-recordset :value="t" :row-class="rowClass" />
            </b-tab>
          </template>
        </b-tabs>
      </template>
    </b-tab>
    <!--
    ============================================================================
    TAB Analisi
    ============================================================================
    -->
    <b-tab v-if="input && input.tables && input.tables.length">
      <template slot="title">
        Analisi
      </template>

      <b-alert variant="danger" show v-if="!output || !output.tables || !output.tables.length">
        <strong>Attenzione:</strong> nessuna analisi ancora eseguita.
      </b-alert>
      <b-alert variant="danger" show v-else-if="dirtyInput">
        <strong>Attenzione:</strong> dati di input modificati. Rieseguire l'analisi.
      </b-alert>

      <div class="text-right">
        <b-dropdown
          v-if="output && output.tables && output.tables.length"
          variant="primary">
          <template slot="button-content">
            <i class="fa fa-fw fa-sign-out"></i>
            Esporta
          </template>
          <b-dropdown-item-button @click.stop.prevent="exportAnalysis(null, 'xlsx')">
            Excel (xlsx)
          </b-dropdown-item-button>
          <b-dropdown-item-button @click.stop.prevent="exportAnalysis(null, 'csv')">
            Testo (csv)
          </b-dropdown-item-button>
        </b-dropdown>
        <b-dropdown
          v-if="!disabled"
          variant="success">
          <template slot="button-content">
            <i class="fa fa-fw fa-area-chart"></i>
            Analizza
          </template>
          <b-dropdown-item-button
            v-if="!output || !output.length || dirtyInput"
            @click.stop.prevent="analyze(null)">
            Analisi semplice
          </b-dropdown-item-button>
          <b-dropdown-item-button
            @click.stop.prevent="analyze(null, true)">
            Analisi forzata
          </b-dropdown-item-button>
        </b-dropdown>
      </div>

      <b-tabs
        v-if="output && output.tables && output.tables.length"
        pills
        content-class="mt-4 mb-4 mx-0 px-0 border-0">
        <b-tab
          title="ANAGRAFICA"
          class="py-0 px-0 mx-0">
          <cr-recordset :value="output.summary" :row-class="rowClass" />
        </b-tab>
        <template v-for="t in output.tables">
          <b-tab
            :title="t.title"
            class="py-0 px-0 mx-0">
            <cr-recordset :value="t" :row-class="rowClass" />
          </b-tab>
        </template>
      </b-tabs>
    </b-tab>
    <!--
    ============================================================================
    TAB Storico
    ============================================================================
    -->
    <b-tab v-if="value.outputHistory">
      <template slot="title">
        Storico
      </template>
      <b-row>
        <b-col class="px-4">
          <data-table
            :columns="extractionHistoryColumns"
            :value="value.extractionHistory"
            title="Storico estrazioni"
            empty-message="Nessuna estrazione ancora presente."
          />
        </b-col>
        <b-col class="px-4">
          <data-table
            :columns="outputHistoryColumns"
            :value="value.outputHistory"
            title="Storico analisi"
            empty-message="Nessuna analisi ancora presente."
          />
        </b-col>
      </b-row>
    </b-tab>
  </b-tabs>
</template>
<script>
import CrAnalysisService from 'app/services/CrAnalysisService';
import { error2text } from 'core/utils';
import BaseAnalysisEditorMixin from 'app/mixins/BaseAnalysisEditorMixin';

export default {
  mixins: [ BaseAnalysisEditorMixin ],

  created() {
    this.service = CrAnalysisService;

    this.uploadedFilesColumns = [
      ...this.uploadedFilesPreColumns,

      { label: 'Denominazione' , value: (item) => item.info.intestatario, class: 'col-2' },
      { label: 'Codice fiscale', value: (item) => item.info.codiceFiscale },
      //{ label: 'CCIAA'         , value: (item) => item.info.sedeLegale },
      //{ label: 'REA'           , value: (item) => item.info.cciaa },
      { label: 'Date richieste', value: (item) => item.info.dateRichieste, class: 'col-2' },
      { label: 'Dimensione'    , value: (item) => item.filesize, type: 'filesize' },

      ...this.uploadedFilesPostColumns
    ];

    this.rowClass = (table, row) => {
      if (table.columns.includes('Da') && table.columns.includes('A')) {
        return undefined;
      }

      if (row["Accordato"] !== undefined && row["Utilizzato"] !== undefined && row["Accordato"] < row["Utilizzato"]) {
        return 'bg-danger';
      }

      if (row["Valore Garanzia"] !== undefined && row["Importo Garantito"] !== undefined && row["Valore Garanzia"] < row["Importo Garantito"]) {
        return 'bg-danger';
      }

      return undefined;
    };
  },

  computed: {
    input() {
      //console.log('input', this.value.input);
      return this.value.input;
    },

    output() {
      //console.log('input', this.value.output);
      return this.value.output;
    },
  },

  methods: {
    // =========================================================================
    // Extraction
    // =========================================================================

    onExtractDataSuccess({ statusCode, data }) {
      console.log('Extraction success:', statusCode, data);
      this.loading = false;

      this.dirtyInput = true;
      this.$refs.extractionAdvice.show();

      const obj = { ...this.value };
      if (data.input) {
        obj.input = data.input;
      }
      if (data.extractionHistory) {
        obj.extractionHistory = data.extractionHistory;
      }
      this.$emit('input', obj);
    },

    // =========================================================================
    // Analysis
    // =========================================================================

    onAnalyzeSuccess({ statusCode, data }) {
      console.log('Analysis success:', statusCode, data);
      this.loading = false;
      this.dirtyInput = false;
      //this.$refs.analysisAdvice.show();

      const obj = { ...this.value };
      if (data.output) {
        obj.output = data.output;
      }
      if (data.outputHistory) {
        obj.outputHistory = data.outputHistory;
      }
      this.$emit('input', obj);
    },

    onGoBackToAnalysisSuccess({ statusCode, data }) {
      console.log('Extraction success:', statusCode, data);
      this.loading = false;
      this.dirtyInput = false;
      //this.$refs.extractionAdvice.show();

      const obj = { ...this.value };
      if (data.input) {
        obj.input = data.input;
      }
      if (data.output) {
        obj.output = data.output;
      }
      if (data.outputHistory) {
        obj.outputHistory = data.outputHistory;
      }
      this.$emit('input', obj);

      this.tabIndex = 2;
    },
  }
}
</script>
